import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import api from "../../services/api";

const TaxasChecklist = () => {
  const theme = useTheme();

  const [tiposLicenca, setTiposLicenca] = useState([]);
  const [tipoLicencaInputValue, setTipoLicencaInputValue] = useState("");
  const [carregandoTiposLicenca, setCarregandoTiposLicenca] = useState(false);

  const [taxas, setTaxas] = useState([]);
  const [loadingLista, setLoadingLista] = useState(false);

  const [tipoLicenca, setTipoLicenca] = useState(null);
  const [formData, setFormData] = useState({
    valorInicial: "",
    valorPorteMedio: "",
    valorPorteGrande: "",
    valorPotencialMedio: "",
    valorPotencialGrande: "",
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [taxaId, setTaxaId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  const buscarTiposLicenca = async (nome) => {
    if (!nome || nome.trim().length < 3) {
      setTiposLicenca([]);
      return;
    }

    setCarregandoTiposLicenca(true);
    try {
      const response = await api.get("TipoLicenca/GetByNomeAsync", {
        params: { nome },
      });
      setTiposLicenca(response.data || []);
    } catch (err) {
      console.error("Erro ao buscar tipos de licença:", err);
      setTiposLicenca([]);
    } finally {
      setCarregandoTiposLicenca(false);
    }
  };

  const carregarTaxas = async () => {
    setLoadingLista(true);
    try {
      const response = await api.get("TaxasDeTipoLicenca/GetAll");
      setTaxas(response.data || []);
      setError(null);
    } catch (err) {
      console.error("Erro ao carregar taxas:", err);
      setError(
        err.response?.data?.message ||
          "Erro ao carregar taxas de tipo de licença"
      );
      setTaxas([]);
    } finally {
      setLoadingLista(false);
    }
  };

  useEffect(() => {
    carregarTaxas();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleValorChange = (field, value) => {
    const apenasNumeros = value.replace(/\D/g, "");
    if (apenasNumeros === "") {
      setFormData((prev) => ({ ...prev, [field]: "" }));
    } else {
      const valorFloat = (parseInt(apenasNumeros) / 100).toFixed(2);
      setFormData((prev) => ({ ...prev, [field]: valorFloat }));
    }
  };

  const handleNovoClick = () => {
    setIsEdit(false);
    setTipoLicenca(null);
    setTipoLicencaInputValue("");
    setFormData({
      valorInicial: "",
      valorPorteMedio: "",
      valorPorteGrande: "",
      valorPotencialMedio: "",
      valorPotencialGrande: "",
    });
    setTaxaId(null);
    setError(null);
    setDialogOpen(true);
  };

  const handleEditarClick = (taxa) => {
    setIsEdit(true);
    setTipoLicenca({
      id: taxa.tipoLicencaId,
      nome: taxa.tipoLicencaNome,
    });
    setTipoLicencaInputValue("");
    setFormData({
      valorInicial: taxa.valorInicial ? taxa.valorInicial.toString() : "",
      valorPorteMedio: taxa.valorAdicionalPorteMedio
        ? taxa.valorAdicionalPorteMedio.toString()
        : "",
      valorPorteGrande: taxa.valorAdicionalPorteGrande
        ? taxa.valorAdicionalPorteGrande.toString()
        : "",
      valorPotencialMedio: taxa.valorAdicionalPotencialMedio
        ? taxa.valorAdicionalPotencialMedio.toString()
        : "",
      valorPotencialGrande: taxa.valorAdicionalPotencialGrande
        ? taxa.valorAdicionalPotencialGrande.toString()
        : "",
    });
    setTaxaId(taxa.id);
    setError(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setTipoLicenca(null);
    setTipoLicencaInputValue("");
    setFormData({
      valorInicial: "",
      valorPorteMedio: "",
      valorPorteGrande: "",
      valorPotencialMedio: "",
      valorPotencialGrande: "",
    });
    setTaxaId(null);
    setError(null);
  };

  const handleSalvar = async () => {
    setLoading(true);
    setError(null);

    const erros = [];

    if (!tipoLicenca || !tipoLicenca.id) {
      erros.push("Tipo de licença é obrigatório");
    }

    if (!formData.valorInicial || parseFloat(formData.valorInicial) < 0) {
      erros.push("Valor inicial é obrigatório e deve ser maior ou igual a 0");
    }

    if (!formData.valorPorteMedio || parseFloat(formData.valorPorteMedio) < 0) {
      erros.push(
        "Valor para porte médio é obrigatório e deve ser maior ou igual a 0"
      );
    }

    if (
      !formData.valorPorteGrande ||
      parseFloat(formData.valorPorteGrande) < 0
    ) {
      erros.push(
        "Valor para porte grande é obrigatório e deve ser maior ou igual a 0"
      );
    }

    if (
      !formData.valorPotencialMedio ||
      parseFloat(formData.valorPotencialMedio) < 0
    ) {
      erros.push(
        "Valor para potencial médio é obrigatório e deve ser maior ou igual a 0"
      );
    }

    if (
      !formData.valorPotencialGrande ||
      parseFloat(formData.valorPotencialGrande) < 0
    ) {
      erros.push(
        "Valor para potencial grande é obrigatório e deve ser maior ou igual a 0"
      );
    }

    if (erros.length > 0) {
      setError(erros.join(". "));
      setLoading(false);
      return;
    }

    try {
      const payload = {
        id: isEdit ? taxaId : null,
        tipoLicencaId: tipoLicenca.id,
        valorInicial: parseFloat(formData.valorInicial),
        valorAdicionalPorteMedio: parseFloat(formData.valorPorteMedio),
        valorAdicionalPorteGrande: parseFloat(formData.valorPorteGrande),
        valorAdicionalPotencialMedio: parseFloat(formData.valorPotencialMedio),
        valorAdicionalPotencialGrande: parseFloat(
          formData.valorPotencialGrande
        ),
      };

      if (isEdit && taxaId) {
        await api.put(`TaxasDeTipoLicenca`, payload);
        setSuccess("Taxa atualizada com sucesso!");
      } else {
        await api.post("TaxasDeTipoLicenca", payload);
        setSuccess("Taxa criada com sucesso!");
      }

      handleDialogClose();
      carregarTaxas();
    } catch (err) {
      console.error("Erro ao salvar taxa:", err);
      setError(
        err.response?.data?.message || "Erro ao salvar taxa de tipo de licença"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeletar = async (id) => {
    if (window.confirm("Tem certeza que deseja deletar esta taxa?")) {
      try {
        await api.delete(`TaxasDeTipoLicenca/${id}`);
        setSuccess("Taxa deletada com sucesso!");
        carregarTaxas();
      } catch (err) {
        console.error("Erro ao deletar taxa:", err);
        setError(
          err.response?.data?.message ||
            "Erro ao deletar taxa de tipo de licença"
        );
      }
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        mt: { xs: 2, md: 4 },
        mb: { xs: 2, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          mb: { xs: 3, md: 4 },
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <AddIcon
          sx={{
            fontSize: { xs: 28, md: 32 },
            mr: { xs: 1, md: 2 },
            color: theme.palette.primary.main,
          }}
        />
        <Typography
          variant="h4"
          component="h1"
          fontWeight="bold"
          color="primary.main"
          sx={{ fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2.125rem" } }}
        >
          Taxas de Tipo de Licença
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<AddIcon />}
          onClick={handleNovoClick}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Nova Taxa
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          overflowX: "auto",
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Table>
          <TableHead
            sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}
          >
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Tipo de Licença</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Valor Inicial (R$)
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Porte Médio (R$)
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Porte Grande (R$)
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Potencial Médio (R$)
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Potencial Grande (R$)
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingLista ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : taxas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  Nenhuma taxa de tipo de licença encontrada
                </TableCell>
              </TableRow>
            ) : (
              taxas.map((taxa) => (
                <TableRow
                  key={taxa.id}
                  sx={{
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <TableCell>{taxa.tipoLicencaNome}</TableCell>
                  <TableCell>
                    {taxa.valorInicial
                      ? `R$ ${taxa.valorInicial.toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {taxa.valorAdicionalPorteMedio
                      ? `R$ ${taxa.valorAdicionalPorteMedio.toLocaleString(
                          "pt-BR",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {taxa.valorAdicionalPorteGrande
                      ? `R$ ${taxa.valorAdicionalPorteGrande.toLocaleString(
                          "pt-BR",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {taxa.valorAdicionalPotencialMedio
                      ? `R$ ${taxa.valorAdicionalPotencialMedio.toLocaleString(
                          "pt-BR",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {taxa.valorAdicionalPotencialGrande
                      ? `R$ ${taxa.valorAdicionalPotencialGrande.toLocaleString(
                          "pt-BR",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}`
                      : "—"}
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{ display: "flex", gap: 1, justifyContent: "center" }}
                    >
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditarClick(taxa)}
                        title="Editar"
                        aria-label={`Editar ${taxa.tipoLicencaNome}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeletar(taxa.id)}
                        title="Deletar"
                        aria-label={`Excluir ${taxa.tipoLicencaNome}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { m: { xs: 2, sm: 3 } },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
          {isEdit
            ? "Editar Taxa de Tipo de Licença"
            : "Nova Taxa de Tipo de Licença"}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Autocomplete
            fullWidth
            disabled={isEdit}
            options={tiposLicenca}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : option?.nome || option?.Nome || ""
            }
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            value={tipoLicenca}
            onChange={(_, newValue) => setTipoLicenca(newValue)}
            inputValue={tipoLicencaInputValue}
            onInputChange={(_, newInput) => {
              setTipoLicencaInputValue(newInput);
              buscarTiposLicenca(newInput);
            }}
            loading={carregandoTiposLicenca}
            noOptionsText={
              tipoLicencaInputValue && tipoLicencaInputValue.trim().length >= 3
                ? "Nenhum tipo de licença encontrado"
                : "Digite 3 ou mais letras"
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipo de Licença"
                placeholder="Digite o nome do tipo de licença"
                margin="normal"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {carregandoTiposLicenca ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Valor Inicial (R$)"
                value={formData.valorInicial}
                onChange={(e) => handleValorChange("valorInicial", e.target.value)}
                placeholder="R$ 0,00"
                size="small"
                inputProps={{
                  inputMode: "numeric",
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Valor Adicional - Porte Médio (R$)"
                value={formData.valorPorteMedio}
                onChange={(e) =>
                  handleValorChange("valorPorteMedio", e.target.value)
                }
                placeholder="R$ 0,00"
                size="small"
                inputProps={{
                  inputMode: "numeric",
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Valor Adicional - Porte Grande (R$)"
                value={formData.valorPorteGrande}
                onChange={(e) =>
                  handleValorChange("valorPorteGrande", e.target.value)
                }
                placeholder="R$ 0,00"
                size="small"
                inputProps={{
                  inputMode: "numeric",
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Valor Adicional - Potencial Médio (R$)"
                value={formData.valorPotencialMedio}
                onChange={(e) =>
                  handleValorChange("valorPotencialMedio", e.target.value)
                }
                placeholder="R$ 0,00"
                size="small"
                inputProps={{
                  inputMode: "numeric",
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Valor Adicional - Potencial Grande (R$)"
                value={formData.valorPotencialGrande}
                onChange={(e) =>
                  handleValorChange("valorPotencialGrande", e.target.value)
                }
                placeholder="R$ 0,00"
                size="small"
                inputProps={{
                  inputMode: "numeric",
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
            "& > button": { width: { xs: "100%", sm: "auto" } },
          }}
        >
          <Button onClick={handleDialogClose}>Cancelar</Button>
          <Button
            onClick={handleSalvar}
            variant="contained"
            disabled={loading || !tipoLicenca}
          >
            {loading ? <CircularProgress size={24} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TaxasChecklist;
